import { useState } from 'react';
import {
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { useExternalLinks } from './api/getExternalLinks';
import GreenButton from 'components/GreenButton';
import DeleteConfirmationDialog from 'components/DeleteConfirmationDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import CreateExternalLinkDialog from './CreateExternalLinkDialog';
import { useCreateExternalLink } from './api/createExternalLink';
import { useDeleteExternalLink } from './api/deleteExternalLink';
import { useUpdateExternalLink } from './api/updateExternalLink';

const ExternalLinkSetup = () => {
  const { data: externalLinks } = useExternalLinks({});
  const createExternalLinkMutation = useCreateExternalLink({});
  const updateExternalLinkMutation = useUpdateExternalLink({});
  const deleteExternalLinkMutation = useDeleteExternalLink({});
  const [selectedLinkId, setSelectedLinkId] = useState<number>(-1);
  const [createLinkDialogOpen, setCreateLinkDialogOpen] =
    useState<boolean>(false);
  const [deleteLinkDialogOpen, setDeleteLinkDialogOpen] =
    useState<boolean>(false);
  const selectedLink = externalLinks?.find(
    (link) => link.id === selectedLinkId
  );

  const handleCreateLink = async ({
    name,
    link,
  }: {
    name: string;
    link: string;
  }) => {
    if (name.trim().length === 0) return;

    if (!selectedLink) {
      await createExternalLinkMutation.mutateAsync({ data: { name, link } });
    } else {
      await updateExternalLinkMutation.mutateAsync({
        linkId: selectedLinkId,
        data: { name, link },
      });
    }
    setSelectedLinkId(-1);
    setCreateLinkDialogOpen(false);
  };

  const handleDeleteLink = async () => {
    await deleteExternalLinkMutation.mutateAsync(selectedLinkId);
  };

  return (
    <>
      <DeleteConfirmationDialog
        open={deleteLinkDialogOpen}
        itemToDelete={`Link ${selectedLink?.name}` || ''}
        onSubmit={handleDeleteLink}
        onClose={() => setDeleteLinkDialogOpen(false)}
      />
      <CreateExternalLinkDialog
        open={createLinkDialogOpen}
        selectedLink={selectedLink}
        handleSubmit={handleCreateLink}
        handleClose={() => setCreateLinkDialogOpen(false)}
      />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <GreenButton
            name='New Link'
            onClick={() => setCreateLinkDialogOpen(true)}
          ></GreenButton>
        </Grid>
        <Grid item xs={6}>
          <List
            dense
            sx={{
              bgcolor: 'background.paper',
              overflow: 'scroll',
            }}
          >
            {externalLinks?.map((link) => (
              <ListItem
                key={link.id}
                secondaryAction={
                  <IconButton
                    edge='end'
                    aria-label='delete'
                    onClick={() => {
                      setSelectedLinkId(link.id);
                      setDeleteLinkDialogOpen(true);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemButton
                  selected={selectedLinkId === link.id}
                  onClick={() => {
                    setSelectedLinkId(link.id);
                    setCreateLinkDialogOpen(true);
                  }}
                >
                  <ListItemText primary={link.name} secondary={link.link} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Grid>
      </Grid>
    </>
  );
};

export default ExternalLinkSetup;
